/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

/**
 * Komunikátor čísel pro Ki-Wi Server.
 */
class CommunicatorNumber {
  /**
   * Načte číslo.
   * @param {number} [value] Číslo ve formátu, který vrací server.
   * @param {Object} [opts={}]
   * @param {null|undefined} [opts.nil] Hodnota pro nevyplněné číslo,
   * která slouží především k postupné migraci z `undefined` na `null`.
   * @param {boolean} [opts.required] Při nastavení na `true` vrací vždy
   * `number`, nebo vyhodí chybu.
   * @returns {null|number|undefined} Číslo.
   */
  static fromNumber(value, { nil, required = false } = {}) {
    if (isNil(value)) {
      if (required) {
        throw new TypeError(`Missing number "${value}".`);
      }
      return nil;
    }
    if (!isNumber(value)) {
      throw new TypeError(`Invalid number "${value}".`);
    }
    return value;
  }

  /**
   * Načte číslo z řetězce.
   * @param {string} [value] Číslo jako řetězec ve formátu, který vrací server.
   * @param {Object} [opts={}]
   * @param {null|undefined} [opts.nil] Hodnota pro nevyplněný nebo prázdný,
   * která slouží především k postupné migraci z `undefined` na `null`.
   * @param {boolean} [opts.required] Při nastavení na `true` vrací vždy
   * `number`, nebo vyhodí chybu.
   * @returns {null|number|undefined} Číslo.
   */
  static fromString(value, { nil, required = false } = {}) {
    if (isNil(value)) {
      if (required) {
        throw new TypeError(`Missing string "${value}".`);
      }
      return nil;
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid string "${value}".`);
    }
    // Odebrání okolního whitespace.
    const trimmed = value.trim();
    // Prázdný string se parsuje jako null, nikoliv jako 0 v algoritmu Number().
    if (!trimmed.length) {
      return nil;
    }
    // Detekce speciálního zápisu čísel: binary, octal, hexadecimal, exponentiation.
    if (trimmed.match(/b|e|o|x/i)) {
      return NaN;
    }
    return Number(trimmed);
  }
}

export default CommunicatorNumber;
